/**
 * v1.3.17 — Deferred AI Jobs Sync Worker (Phase 2A).
 *
 * Drains the deferred-AI-jobs queue when the app becomes effectively
 * online (network up AND manual Airplane Mode off). For each queued
 * job, invokes the SAME axios endpoint the online button would have
 * hit — so server-side behavior is byte-identical to a live click.
 *
 * DESIGN INVARIANTS
 * -----------------
 * 1. Uses `subscribeConnectivity` from lib/connectivity.js, NOT raw
 *    browser events. So both the "network came back" transition
 *    AND the "user turned off manual airplane" transition trigger
 *    a drain — through a single code path.
 * 2. Endpoint resolution lives in a single dispatch table so adding
 *    a new deferrable action means one table row, no branching.
 * 3. Toasts fire per-batch, not per-job, so a drain of 20 jobs
 *    doesn't nuke the user with 20 toasts.
 * 4. Jobs that fail 5+ times are left in the store in `failed`
 *    state for the queue drawer (Phase 2B) to surface.
 */
import axios from 'axios';
import { toast } from 'sonner';

import {
  listPendingDeferredJobs,
  updateDeferredJobStatus,
  removeDeferredJob,
  publishDeferredJobsChanged,
} from './deferredAiJobsQueue';
import { subscribeConnectivity, isEffectivelyOnline } from '../lib/connectivity';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Attempt cap — after this many retries the job stays in `failed`
// state, visible in the queue drawer (Phase 2B) for manual retry
// or discard.
const MAX_RETRIES = 5;

// ---------------------------------------------------------------------------
// Dispatch table
// ---------------------------------------------------------------------------
// Each entry: { url, method, body, timeout } — resolved per-job at drain time.
// Adding a new deferrable AI action is a single row here.
const JOB_DISPATCH = {
  find_manuals: ({ asset_id }) => ({
    url: `${API}/assets/${asset_id}/find-manuals`,
    method: 'post',
    body: {},
    timeout: 60000,
  }),
  reanalyze_asset: ({ analysis_id }) => ({
    url: `${API}/analyses/${analysis_id}/reanalyze`,
    method: 'post',
    body: {},
    timeout: 60000,
  }),
  // v1.3.18 — bulk reanalysis at assessment level; long-running,
  // launched async on server, poll status separately.
  bulk_reanalyze: ({ assessment_id }) => ({
    url: `${API}/assessments/${assessment_id}/bulk-reanalyze`,
    method: 'post',
    body: {},
    timeout: 60000,
  }),
  site_review: ({ assessment_id, images_base64, notes, ...rest }) => ({
    url: `${API}/site-review/analyze`,
    method: 'post',
    body: { assessment_id, images_base64, notes, ...rest },
    timeout: 180000,
  }),
  assess_damage: ({ assessment_id, images_base64, ...rest }) => ({
    url: `${API}/assessments/${assessment_id}/assess-damage`,
    method: 'post',
    body: { images_base64, ...rest },
    timeout: 180000,
  }),
  eul_enrich: ({ asset_id }) => ({
    url: `${API}/assets/${asset_id}/enrich-eul`,
    method: 'post',
    body: {},
    timeout: 45000,
  }),
  detect_siblings: ({ assessment_id }) => ({
    url: `${API}/assessments/${assessment_id}/detect-siblings`,
    method: 'post',
    body: {},
    timeout: 60000,
  }),
};

const _authHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : undefined;
};

let isRunning = false;
let isInitialized = false;

const _runOne = async (job) => {
  const spec = JOB_DISPATCH[job.job_type];
  if (!spec) {
    await updateDeferredJobStatus(job.id, 'failed', `Unknown job_type: ${job.job_type}`);
    return { ok: false };
  }
  const { url, method, body, timeout } = spec(job.params || {});
  try {
    await updateDeferredJobStatus(job.id, 'running');
    const resp = await axios({
      method,
      url,
      data: body,
      timeout,
      headers: _authHeader(),
    });
    if (resp.status >= 200 && resp.status < 300) {
      await updateDeferredJobStatus(job.id, 'completed');
      // Housekeeping — remove completed rows so the store stays small.
      // Anything that fails stays for the drawer.
      await removeDeferredJob(job.id);
      return { ok: true };
    }
    await updateDeferredJobStatus(job.id, 'failed', `HTTP ${resp.status}`);
    return { ok: false };
  } catch (err) {
    const msg = err?.response?.data?.detail || err?.message || 'unknown';
    await updateDeferredJobStatus(job.id, 'failed', msg);
    return { ok: false };
  }
};

// v1.3.18 — Public single-job runner. Used by the queue drawer's
// per-row "Run Now" button. Same code path as the batch drain — just
// one job at a time. Intentionally bypasses the isEffectivelyOnline
// gate because "Run Now" is an explicit user override.
export const runOneDeferredJob = _runOne;

export const drainDeferredJobs = async () => {
  if (isRunning) return;
  if (!isEffectivelyOnline()) return;
  isRunning = true;
  try {
    const pending = await listPendingDeferredJobs();
    if (pending.length === 0) return;

    let succeeded = 0;
    let failed = 0;
    for (const job of pending) {
      // Retry cap.
      if ((job.retry_count || 0) >= MAX_RETRIES) continue;
      // Re-check connectivity between jobs — a mid-drain toggle-off
      // shouldn't burn through the queue.
      if (!isEffectivelyOnline()) break;
      // eslint-disable-next-line no-await-in-loop
      const res = await _runOne(job);
      if (res.ok) succeeded += 1;
      else failed += 1;
    }

    if (succeeded > 0) {
      toast.success(
        `Ran ${succeeded} queued AI task${succeeded > 1 ? 's' : ''}`,
        { duration: 4500 },
      );
    }
    if (failed > 0) {
      toast.warning(
        `${failed} queued AI task${failed > 1 ? 's' : ''} failed — check the queue drawer`,
        { duration: 5500 },
      );
    }
    publishDeferredJobsChanged();
  } finally {
    isRunning = false;
  }
};

export const initDeferredAiJobsSync = () => {
  if (isInitialized) return;
  isInitialized = true;

  subscribeConnectivity(({ effectiveOnline }) => {
    if (effectiveOnline) {
      // Grace period so a network flap doesn't cause a stampede.
      setTimeout(() => { drainDeferredJobs(); }, 900);
    }
  });

  // If the app boots effectively-online, drain immediately after a
  // small delay to let the auth token be present.
  if (isEffectivelyOnline()) {
    setTimeout(() => { drainDeferredJobs(); }, 2500);
  }
};
